import type { SiteLanguageCode } from "@/lib/language";

type Dict<T> = Record<SiteLanguageCode, T>;

export const achievementsCopy: Dict<{
  eyebrow: string;
  title: string;
  subtitle: string;
  viewDetails: string;
  highlights: string;
  close: string;
  detailsAria: string;
  loadingAria: string;
  items: {
    title: string;
    caption: string;
    organizer: string;
    description: string;
    highlights: string[];
  }[];
}> = {
  en: {
    eyebrow: "Our Achievements",
    title: "Recognitions that inspire us to keep building.",
    subtitle: "Tap any achievement to explore the story behind it.",
    viewDetails: "View details",
    highlights: "Highlights",
    close: "Close",
    detailsAria: "Achievement details",
    loadingAria: "Loading achievement details",
    items: [
      {
        title: "Hackathon 2.0 Champions",
        caption: "Government of India · 2026",
        organizer: "Government of India",
        description:
          "National champions at Hackathon 2.0 - recognized for building a production-ready solution under intense time constraints, with clear architecture, measurable impact, and polished delivery.",
        highlights: [
          "National-level championship win",
          "End-to-end product shipped in 36 hours",
          "Judged on impact, scalability, and execution",
        ],
      },
      {
        title: "SIH 2025 Winners",
        caption: "Smart India Hackathon · 2025",
        organizer: "Smart India Hackathon",
        description:
          "Winners of Smart India Hackathon 2025. Our team delivered a government-aligned digital solution focused on reliability, accessibility, and real operational value for citizens and administrators.",
        highlights: [
          "SIH 2025 winning team",
          "Problem statement solved for a real agency use-case",
          "Emphasis on maintainable, deployable software",
        ],
      },
      {
        title: "TechXcelerate Winners",
        caption: "BITS Pilani, Goa · 2025",
        organizer: "BITS Pilani, Goa",
        description:
          "Champions at TechXcelerate (BITS Pilani, Goa) - recognized for technical depth, product clarity, and the ability to turn an ambitious idea into a working system.",
        highlights: [
          "Campus flagship tech competition win",
          "Strong system design and UX execution",
          "Peer and mentor recognition for engineering quality",
        ],
      },
      {
        title: "Build With India Semifinalists",
        caption: "Google India · 2025",
        organizer: "Google India",
        description:
          "Semifinalists in Build With India by Google India - competing among teams building for scale, inclusion, and meaningful technology outcomes across India.",
        highlights: [
          "Google India program semifinalists",
          "Focus on scalable, inclusive product thinking",
          "Mentorship and feedback from industry reviewers",
        ],
      },
      {
        title: "SIH 2024 Finalists",
        caption: "Smart India Hackathon · 2024",
        organizer: "Smart India Hackathon",
        description:
          "Reached the national finals of Smart India Hackathon 2024 - refining our approach to rapid prototyping, stakeholder communication, and engineering under pressure.",
        highlights: [
          "National finals selection",
          "Strong product narrative and demo",
          "Foundation for the following year’s championship",
        ],
      },
    ],
  },
  hi: {
    eyebrow: "हमारी उपलब्धियाँ",
    title: "वे सम्मान जो हमें बनाते रहने की प्रेरणा देते हैं।",
    subtitle: "किसी भी उपलब्धि पर टैप करें और उसकी कहानी जानें।",
    viewDetails: "विवरण देखें",
    highlights: "मुख्य बिंदु",
    close: "बंद करें",
    detailsAria: "उपलब्धि विवरण",
    loadingAria: "उपलब्धि विवरण लोड हो रहा है",
    items: [
      {
        title: "हैकथॉन 2.0 चैंपियन",
        caption: "भारत सरकार · 2026",
        organizer: "भारत सरकार",
        description:
          "हैकथॉन 2.0 में राष्ट्रीय चैंपियन - तीव्र समयसीमा में प्रोडक्शन-रेडी समाधान बनाने, स्पष्ट आर्किटेक्चर, मापने योग्य प्रभाव और परिष्कृत डिलीवरी के लिए सम्मानित।",
        highlights: [
          "राष्ट्रीय स्तर की चैंपियनशिप जीत",
          "36 घंटे में एंड-टू-एंड प्रोडक्ट शिप",
          "प्रभाव, स्केलेबिलिटी और निष्पादन पर मूल्यांकन",
        ],
      },
      {
        title: "SIH 2025 विजेता",
        caption: "स्मार्ट इंडिया हैकथॉन · 2025",
        organizer: "स्मार्ट इंडिया हैकथॉन",
        description:
          "स्मार्ट इंडिया हैकथॉन 2025 के विजेता। हमारी टीम ने विश्वसनीयता, सुलभता और नागरिकों व प्रशासकों के लिए वास्तविक परिचालन मूल्य पर केंद्रित सरकारी-संरेखित डिजिटल समाधान दिया।",
        highlights: [
          "SIH 2025 विजेता टीम",
          "वास्तविक एजेंसी उपयोग-मामले के लिए समस्या हल",
          "मेंटेन करने योग्य, डिप्लॉय करने योग्य सॉफ़्टवेयर पर ज़ोर",
        ],
      },
      {
        title: "TechXcelerate विजेता",
        caption: "BITS पिलानी, गोवा · 2025",
        organizer: "BITS पिलानी, गोवा",
        description:
          "TechXcelerate (BITS पिलानी, गोवा) में चैंपियन - तकनीकी गहराई, प्रोडक्ट स्पष्टता और महत्वाकांक्षी विचार को काम करने वाले सिस्टम में बदलने की क्षमता के लिए सम्मानित।",
        highlights: [
          "कैंपस फ्लैगशिप टेक प्रतियोगिता जीत",
          "मज़बूत सिस्टम डिज़ाइन और UX निष्पादन",
          "इंजीनियरिंग गुणवत्ता के लिए साथी और मेंटर मान्यता",
        ],
      },
      {
        title: "Build With India सेमीफाइनलिस्ट",
        caption: "Google India · 2025",
        organizer: "Google India",
        description:
          "Google India के Build With India में सेमीफाइनलिस्ट - भारत भर में स्केल, समावेश और सार्थक तकनीकी परिणामों के लिए बना रही टीमों के बीच प्रतिस्पर्धा।",
        highlights: [
          "Google India कार्यक्रम सेमीफाइनलिस्ट",
          "स्केलेबल, समावेशी प्रोडक्ट सोच पर ध्यान",
          "उद्योग समीक्षकों से मेंटरशिप और फीडबैक",
        ],
      },
      {
        title: "SIH 2024 फाइनलिस्ट",
        caption: "स्मार्ट इंडिया हैकथॉन · 2024",
        organizer: "स्मार्ट इंडिया हैकथॉन",
        description:
          "स्मार्ट इंडिया हैकथॉन 2024 के राष्ट्रीय फाइनल तक पहुँचे - तीव्र प्रोटोटाइपिंग, हितधारक संचार और दबाव में इंजीनियरिंग के हमारे दृष्टिकोण को परिष्कृत किया।",
        highlights: [
          "राष्ट्रीय फाइनल चयन",
          "मज़बूत प्रोडक्ट कथा और डेमो",
          "अगले वर्ष की चैंपियनशिप की नींव",
        ],
      },
    ],
  },
} as const;

export const statsBarCopy: Dict<{
  labels: [string, string, string, string];
}> = {
  en: {
    labels: [
      "Products Built",
      "Clients Served",
      "Recognitions",
      "Uptime Delivered",
    ],
  },
  hi: {
    labels: [
      "उत्पाद बनाए",
      "क्लाइंट सेवाएँ",
      "सम्मान",
      "अपटाइम डिलीवर",
    ],
  },
} as const;

export const whoWeAreCopy: Dict<{
  eyebrow: string;
  title: string;
  p1: string;
  p2: string;
  p3: string;
  valuesTitle: string;
  capabilities: [string, string, string, string];
  values: { title: string; description: string }[];
}> = {
  en: {
    eyebrow: "Who We Are",
    title: "A product & SaaS company building the future, today.",
    p1: "DevDotCom exists to empower teams with tools and systems that eliminate friction and amplify productivity - from idea to production.",
    p2: "We believe great software is built by people with the right tools, clear processes, and unwavering focus. Our mission is to provide the infrastructure that lets you concentrate on what matters: building exceptional products.",
    p3: "Whether you need a greenfield platform, a modernization program, or a long-term engineering partner, we bring the same standard: precise execution, honest communication, and software that holds up in production.",
    valuesTitle: "Our Values",
    capabilities: [
      "SaaS Platforms",
      "Custom Software",
      "Cloud Systems",
      "AI Solutions",
    ],
    values: [
      {
        title: "Engineering-first",
        description: "We solve problems through code, not decks.",
      },
      {
        title: "Clarity over complexity",
        description: "Systems should be understandable and maintainable.",
      },
      {
        title: "Shipping over slides",
        description: "Working software is the only measure of progress.",
      },
      {
        title: "Ownership beyond delivery",
        description: "We stay accountable long after launch.",
      },
      {
        title: "Quality as a discipline",
        description: "Excellence is embedded in every decision.",
      },
    ],
  },
  hi: {
    eyebrow: "हम कौन हैं",
    title: "एक प्रोडक्ट और SaaS कंपनी जो आज ही भविष्य बना रही है।",
    p1: "DevDotCom टीमों को ऐसे टूल्स और सिस्टम देने के लिए मौजूद है जो घर्षण हटाएँ और उत्पादकता बढ़ाएँ - विचार से प्रोडक्शन तक।",
    p2: "हम मानते हैं कि बेहतरीन सॉफ़्टवेयर सही टूल्स, स्पष्ट प्रक्रियाओं और अडिग फोकस वाले लोगों से बनता है। हमारा मिशन वह इन्फ्रास्ट्रक्चर देना है जिससे आप असली काम पर ध्यान दें: असाधारण उत्पाद बनाना।",
    p3: "चाहे आपको नया प्लेटफ़ॉर्म चाहिए, आधुनिकीकरण कार्यक्रम, या दीर्घकालिक इंजीनियरिंग पार्टनर - हम वही मानक लाते हैं: सटीक निष्पादन, ईमानदार संचार, और प्रोडक्शन में टिकने वाला सॉफ़्टवेयर।",
    valuesTitle: "हमारे मूल्य",
    capabilities: [
      "SaaS प्लेटफ़ॉर्म",
      "कस्टम सॉफ़्टवेयर",
      "क्लाउड सिस्टम",
      "AI समाधान",
    ],
    values: [
      {
        title: "इंजीनियरिंग-प्रथम",
        description: "हम समस्याओं को कोड से हल करते हैं, स्लाइड्स से नहीं।",
      },
      {
        title: "जटिलता से ऊपर स्पष्टता",
        description: "सिस्टम समझने योग्य और मेंटेन करने योग्य होने चाहिए।",
      },
      {
        title: "स्लाइड्स से ऊपर शिपिंग",
        description: "काम करने वाला सॉफ़्टवेयर ही प्रगति का मापदंड है।",
      },
      {
        title: "डिलीवरी से आगे स्वामित्व",
        description: "लॉन्च के बहुत बाद तक हम ज़िम्मेदार रहते हैं।",
      },
      {
        title: "अनुशासन के रूप में गुणवत्ता",
        description: "उत्कृष्टता हर निर्णय में अंतर्निहित है।",
      },
    ],
  },
} as const;

export const howWeWorkCopy: Dict<{
  eyebrow: string;
  title: string;
  subtitle: string;
  steps: { title: string; description: string }[];
}> = {
  en: {
    eyebrow: "How We Work",
    title: "A calm process built for complex products.",
    subtitle:
      "Predictable collaboration without the ceremony - so momentum stays high and quality stays non-negotiable.",
    steps: [
      {
        title: "Discover",
        description:
          "We map constraints, users, and success criteria before a single line of production code ships.",
      },
      {
        title: "Design & Architect",
        description:
          "Clear interfaces, pragmatic stack choices, and a path that teams can actually maintain.",
      },
      {
        title: "Build & Ship",
        description:
          "Iterative delivery with visible progress - demos, feedback loops, and production discipline.",
      },
      {
        title: "Own & Evolve",
        description:
          "We stay close after launch: monitoring, iteration, and the operational care software deserves.",
      },
    ],
  },
  hi: {
    eyebrow: "हम कैसे काम करते हैं",
    title: "जटिल उत्पादों के लिए बनाई गई शांत प्रक्रिया।",
    subtitle:
      "बिना अनावश्यक औपचारिकता के अनुमान योग्य सहयोग - ताकि गति बनी रहे और गुणवत्ता पर कोई समझौता न हो।",
    steps: [
      {
        title: "डिस्कवर",
        description:
          "प्रोडक्शन कोड की एक भी पंक्ति से पहले हम बाधाएँ, उपयोगकर्ता और सफलता के मानदंड मैप करते हैं।",
      },
      {
        title: "डिज़ाइन और आर्किटेक्ट",
        description:
          "स्पष्ट इंटरफ़ेस, व्यावहारिक स्टैक चुनाव, और एक ऐसा रास्ता जिसे टीमें वास्तव में मेंटेन कर सकें।",
      },
      {
        title: "बिल्ड और शिप",
        description:
          "दिखती प्रगति के साथ पुनरावृत्तीय डिलीवरी - डेमो, फीडबैक लूप और प्रोडक्शन अनुशासन।",
      },
      {
        title: "स्वामित्व और विकास",
        description:
          "लॉन्च के बाद भी करीब रहते हैं: मॉनिटरिंग, सुधार, और सॉफ़्टवेयर के योग्य परिचालन देखभाल।",
      },
    ],
  },
} as const;

export const teamCopy: Dict<{
  eyebrow: string;
  title: string;
  subtitle: string;
  bios: [string, string, string];
}> = {
  en: {
    eyebrow: "Our Leadership",
    title: "The minds driving DevDotCom.",
    subtitle:
      "A compact leadership team with shared ownership of product, engineering, and delivery.",
    bios: [
      "Leads product vision and engineering direction across DevDotCom - aligning strategy with systems that ship.",
      "Builds scalable architectures and keeps delivery grounded in reliability, performance, and craft.",
      "Drives product execution and client-facing excellence - turning roadmaps into outcomes teams can trust.",
    ],
  },
  hi: {
    eyebrow: "हमारा नेतृत्व",
    title: "वे दिमाग जो DevDotCom चलाते हैं।",
    subtitle:
      "प्रोडक्ट, इंजीनियरिंग और डिलीवरी के साझा स्वामित्व वाली एक छोटी नेतृत्व टीम।",
    bios: [
      "DevDotCom भर में प्रोडक्ट विज़न और इंजीनियरिंग दिशा का नेतृत्व - रणनीति को शिप होने वाले सिस्टम से जोड़ते हैं।",
      "स्केलेबल आर्किटेक्चर बनाते हैं और डिलीवरी को विश्वसनीयता, परफ़ॉर्मेंस और क्राफ्ट में जमीं रखते हैं।",
      "प्रोडक्ट निष्पादन और क्लाइंट-फेसिंग उत्कृष्टता चलाते हैं - रोडमैप को भरोसेमंद परिणामों में बदलते हैं।",
    ],
  },
} as const;

export const aboutCtaCopy: Dict<{
  eyebrow: string;
  titleBefore: string;
  titleAccent: string;
  body: string;
  primaryCta: string;
  secondaryCta: string;
}> = {
  en: {
    eyebrow: "Next chapter",
    titleBefore: "Building technology for a",
    titleAccent: "better tomorrow.",
    body: "Let's turn your vision into reality - with engineering excellence and long-term impact.",
    primaryCta: "Let's Build Together",
    secondaryCta: "View our work",
  },
  hi: {
    eyebrow: "अगला अध्याय",
    titleBefore: "तकनीक बना रहे हैं एक",
    titleAccent: "बेहतर कल के लिए।",
    body: "आइए आपके विज़न को वास्तविकता बनाएँ - इंजीनियरिंग उत्कृष्टता और दीर्घकालिक प्रभाव के साथ।",
    primaryCta: "साथ मिलकर बनाएँ",
    secondaryCta: "हमारा कार्य देखें",
  },
} as const;
